import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Lenis from "lenis";
import GrainOverlay from "@/components/GrainOverlay";
import HeroSection from "@/components/HeroSection";
import EditorialMarquee from "@/components/EditorialMarquee";
import MusicSection from "@/components/MusicSection";
import ManifestoSection from "@/components/ManifestoSection";
import InstagramGrid from "@/components/InstagramGrid";
import StreamingFooter from "@/components/StreamingFooter";
import AudioPlayer from "@/components/AudioPlayer";
import LyricsPage from "@/components/LyricsPage";

const SINGLE_ART =
    "https://customer-assets-cm19k8pv.emergentagent.net/job_bejnni-artist/artifacts/w9wohi7z_B348D4E2-AC04-424D-98B4-3B8B7858F4C8.jpeg";
const ALBUM_ART =
    "https://customer-assets-cm19k8pv.emergentagent.net/job_bejnni-artist/artifacts/mh1gvuyv_B790DFCB-4F0B-4A07-8B0E-4AD3D8AC60A6.png";

const TRACKS = [
    {
        title: "dead flowers",
        length: "2:53",
        url: "https://customer-assets-cm19k8pv.emergentagent.net/job_bejnni-artist/artifacts/mk364fzk_deadflowers.mp3",
        lyrics: "/lyrics/dead-flowers",
    },
    {
        title: "…every broken piece i've known",
        length: "3:59",
        url: "https://customer-assets-cm19k8pv.emergentagent.net/job_bejnni-artist/artifacts/vh0cf72t_%E2%80%A6every%20broken%20piece%20i%E2%80%99ve%20known.mp3",
    },
    {
        title: "i bloom better when im dead",
        length: "3:49",
        url: "https://customer-assets-cm19k8pv.emergentagent.net/job_bejnni-artist/artifacts/pgut5a9k_i%20bloom%20better%20when%20im%20dead.mp3",
        artwork: SINGLE_ART,
    },
    {
        title: "CANNIBALISM",
        length: "8:00",
        url: "https://customer-assets-cm19k8pv.emergentagent.net/job_bejnni-artist/artifacts/eunz80oe_CANNIBALISM.mp3",
        artwork: ALBUM_ART,
    },
    {
        title: "cigarettes lullabies",
        length: "5:00",
        url: "https://customer-assets-cm19k8pv.emergentagent.net/job_bejnni-artist/artifacts/8xp3dun3_cigarettes%20lullabies.mp3",
    },
];

function App() {
    const lenisRef = useRef(null);
    const audioRef = useRef(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [time, setTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(0.8);

    useEffect(() => {
        const lenis = new Lenis({ duration: 1.2, smoothWheel: true });
        lenisRef.current = lenis;
        let raf;
        const loop = (t) => {
            lenis.raf(t);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
        };
    }, []);

    useEffect(() => {
        const audio = audioRef.current;
        audio.src = TRACKS[currentIndex].url;
        audio.volume = volume;
        setTime(0);
        if (isPlaying) audio.play().catch(() => setIsPlaying(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentIndex]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio.src) return;
        if (isPlaying) audio.play().catch(() => setIsPlaying(false));
        else audio.pause();
    }, [isPlaying]);

    const scrollTo = (id) => {
        if (lenisRef.current) lenisRef.current.scrollTo(id, { duration: 1.6 });
    };

    const selectTrack = (i) => {
        if (i === currentIndex) {
            setIsPlaying((p) => !p);
        } else {
            setCurrentIndex(i);
            setIsPlaying(true);
        }
    };

    const next = () => setCurrentIndex((i) => (i + 1) % TRACKS.length);
    const prev = () =>
        setCurrentIndex((i) => (i - 1 + TRACKS.length) % TRACKS.length);

    const seek = (ratio) => {
        const audio = audioRef.current;
        if (!audio.duration) return;
        audio.currentTime = ratio * audio.duration;
        setTime(audio.currentTime);
    };

    const changeVolume = (v) => {
        setVolume(v);
        audioRef.current.volume = v;
    };

    return (
        <BrowserRouter>
            <div className="relative bg-void text-bone">
                <GrainOverlay />
                <audio
                    ref={audioRef}
                    data-testid="audio-element"
                    onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
                    onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
                    onEnded={next}
                />
                <Routes>
                    <Route
                        path="/"
                        element={
                            <>
                                <HeroSection onNavigate={scrollTo} />
                                <EditorialMarquee />
                                <MusicSection
                                    tracks={TRACKS}
                                    currentIndex={currentIndex}
                                    isPlaying={isPlaying}
                                    onSelect={selectTrack}
                                />
                                <ManifestoSection />
                                <InstagramGrid />
                                <StreamingFooter />
                            </>
                        }
                    />
                    <Route
                        path="/lyrics/dead-flowers"
                        element={
                            <LyricsPage
                                isPlaying={isPlaying && currentIndex === 0}
                                onPlay={() => selectTrack(0)}
                            />
                        }
                    />
                </Routes>
                <AudioPlayer
                    track={TRACKS[currentIndex]}
                    isPlaying={isPlaying}
                    time={time}
                    duration={duration}
                    volume={volume}
                    onToggle={() => setIsPlaying((p) => !p)}
                    onNext={next}
                    onPrev={prev}
                    onSeek={seek}
                    onVolume={changeVolume}
                />
            </div>
        </BrowserRouter>
    );
}

export default App;
